import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, forkJoin, map, of, catchError } from 'rxjs';
import { EmbedContextService } from './services/embed-context.service';
import { baseLanguageCode } from './services/helpers/language.util';
import { environment } from 'src/environments/environment';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

/** Fusión profunda: `source` gana por clave hoja; arrays y no-objetos se reemplazan. */
function deepMerge(target: Record<string, unknown>, source: unknown): Record<string, unknown> {
  if (!isPlainObject(source)) return target;
  const result: Record<string, unknown> = { ...target };
  for (const [key, value] of Object.entries(source)) {
    result[key] = isPlainObject(value) && isPlainObject(result[key])
      ? deepMerge(result[key] as Record<string, unknown>, value)
      : value;
  }
  return result;
}

@Injectable()
export class FallbackTranslateLoader implements TranslateLoader {
  private http = inject(HttpClient);
  private _embedContext = inject(EmbedContextService);
  private remoteBase = environment.langsBaseUrl;
  private localBase = '/assets/i18n/';

  getTranslation(lang: string): Observable<any> {
    // Fallback regional (HRS-2934): un idioma con región (pt-BR) no tiene JSON
    // local propio, así que su diccionario parte del idioma base (pt) y las
    // fuentes de la variante solo PISAN lo que traigan. Sin esto, la interfaz de
    // un tenant pt-BR se quedaba con las claves crudas (= textos en español).
    const base = baseLanguageCode(lang);
    const sources: Observable<object>[] = [];

    if (base && base !== lang) {
      sources.push(this.local$(base));
      if (!this._embedContext.isEmbed())
        sources.push(this.remote$(base));
    }

    // En el widget embebido (cross-origin) el remoto da CORS; solo local
    // para no ensuciar la consola.
    sources.push(this.local$(lang));
    if (!this._embedContext.isEmbed())
      sources.push(this.remote$(lang));

    // FUSIÓN local←remoto (y base←variante): el remoto (CMS) manda por clave
    // HOJA, pero el local (assets/i18n del build) RELLENA los huecos que el CMS
    // no tenga, también dentro de objetos anidados (HRS-3482: con un
    // Object.assign superficial el `reservation` del CMS sustituía entero al
    // local y las claves nuevas anidadas salían crudas). Antes era remoto-O-local (solo caía a local si el remoto fallaba
    // ENTERO), así que las claves ausentes en el CMS caían al fallback = la
    // clave en español (visible p.ej. en el planner por iframe, donde Google
    // Translate no las tapa).
    return forkJoin(sources).pipe(
      map((parts) => parts.reduce<Record<string, unknown>>((acc, part) => deepMerge(acc, part), {})),
    );
  }

  private local$(lang: string): Observable<object> {
    return this.http.get(`${this.localBase}${lang}.json`).pipe(catchError(() => of({})));
  }

  private remote$(lang: string): Observable<object> {
    return this.http.get(`${this.remoteBase}${lang}.php`).pipe(catchError(() => of({})));
  }
}